/**
 * Brief 3.11 — Super-admin system settings.
 *
 * Lets a super-admin tune the four real-money knobs without touching
 * `.env`: daily extraction cap (per merchant), re-run cooldown,
 * lifetime LLM token budget (per merchant), and the org-wide daily
 * USD budget. Each setting shows: current value, default, env
 * override (if any), and inline edit. Today's usage snapshot lives
 * at the top so the admin can see how close they are to the limits.
 * Audit trail (last 50 changes) is at the bottom.
 *
 * Backed by GET/PUT /admin/system/settings and
 * GET /admin/system/settings/audit. Super-admin only; the route is
 * also gated server-side (returns 403 for regular admins on PUT).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Settings, AlertTriangle, Check, Loader2, History, RotateCcw } from "lucide-react";
import api from "../../lib/api";


function StatTile({ label, value, hint, accent }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] px-4 py-3"
         data-testid={`stat-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className={`mt-1 text-[19px] font-semibold ${accent || "text-white"}`}>{value}</div>
      {hint && <div className="mt-1 text-[11px] text-zinc-500">{hint}</div>}
    </div>
  );
}


function SettingCard({ setting, onSave, saving }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(setting.current_value));
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    setDraft(String(setting.current_value));
  }, [setting.current_value]);

  const onCancel = () => {
    setEditing(false);
    setDraft(String(setting.current_value));
    setReason("");
    setErr("");
  };

  const onSubmit = async () => {
    setErr("");
    try {
      await onSave(setting.key, draft, reason);
      setEditing(false);
      setReason("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Save failed");
    }
  };

  const isDirty = String(draft) !== String(setting.current_value);
  const isCustom = setting.is_db_override;

  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5"
         data-testid={`setting-card-${setting.key}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-[14px] font-medium text-white">{setting.label}</h3>
          <p className="mt-1 text-[12.5px] text-zinc-400 leading-relaxed">{setting.description}</p>
        </div>
        {isCustom && (
          <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-400/[0.06] text-amber-200 px-2 py-0.5 text-[10.5px]"
                title={`Last changed ${setting.updated_at} by ${setting.updated_by}`}>
            <RotateCcw className="h-3 w-3" /> Custom
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Current</div>
          <div className="mt-0.5 text-white text-[15px] font-semibold tabular-nums"
               data-testid={`setting-current-${setting.key}`}>
            {setting.kind === "float"
              ? Number(setting.current_value).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
              : Number(setting.current_value).toLocaleString()}
            {setting.units && <span className="ml-1 text-[11px] text-zinc-500 font-normal">{setting.units}</span>}
          </div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Default</div>
          <div className="mt-0.5 text-zinc-400 text-[13px] tabular-nums">
            {setting.kind === "float"
              ? Number(setting.default).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
              : Number(setting.default).toLocaleString()}
          </div>
        </div>
      </div>

      {setting.env_override && (
        <div className="mt-3 text-[11px] text-zinc-500">
          Env override available: <code className="text-zinc-300">{setting.env_var}={setting.env_override}</code> (used only when no DB value)
        </div>
      )}

      {!editing && (
        <div className="mt-4 flex items-center gap-2">
          <button data-testid={`setting-edit-${setting.key}`}
                  onClick={() => setEditing(true)}
                  className="text-[12px] px-3 py-1.5 rounded-lg bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/30">
            Change
          </button>
          {setting.updated_at && (
            <span className="text-[11px] text-zinc-500">
              Last changed by <span className="text-zinc-300">{setting.updated_by}</span> on {new Date(setting.updated_at).toLocaleString()}
            </span>
          )}
        </div>
      )}

      {editing && (
        <div className="mt-4 space-y-3" data-testid={`setting-edit-form-${setting.key}`}>
          <div>
            <label className="text-[11px] text-zinc-500 block mb-1">New value</label>
            <input
              type="number"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              min={setting.min_value ?? undefined}
              max={setting.max_value ?? undefined}
              step={setting.kind === "float" ? "0.01" : "1"}
              data-testid={`setting-input-${setting.key}`}
              className="w-full rounded-lg bg-black/40 border border-white/[0.08] px-3 py-1.5 text-[14px] text-white tabular-nums"
            />
            <div className="text-[10.5px] text-zinc-500 mt-1">
              Range: {setting.min_value?.toLocaleString()} – {setting.max_value?.toLocaleString()} {setting.units}
            </div>
          </div>
          <div>
            <label className="text-[11px] text-zinc-500 block mb-1">Reason (optional, audit-logged)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={400}
              placeholder="e.g. raising cap for high-volume Q1 cohort"
              data-testid={`setting-reason-${setting.key}`}
              className="w-full rounded-lg bg-black/40 border border-white/[0.08] px-3 py-1.5 text-[12.5px] text-white"
            />
          </div>
          {err && (
            <div className="text-[11.5px] text-rose-300 bg-rose-400/10 border border-rose-400/30 rounded-lg px-3 py-2"
                 data-testid={`setting-error-${setting.key}`}>
              <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
              {err}
            </div>
          )}
          <div className="flex items-center gap-2">
            <button data-testid={`setting-save-${setting.key}`}
                    disabled={saving || !isDirty}
                    onClick={onSubmit}
                    className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg bg-emerald-400/10 hover:bg-emerald-400/20 text-emerald-200 border border-emerald-400/30 disabled:opacity-50">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Save
            </button>
            <button data-testid={`setting-cancel-${setting.key}`}
                    onClick={onCancel}
                    className="text-[12px] px-3 py-1.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-zinc-200 border border-white/[0.10]">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


export default function AdminSystemSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: settingsData }, { data: auditData }] = await Promise.all([
        api.get("/admin/system/settings"),
        api.get("/admin/system/settings/audit?limit=50"),
      ]);
      setData(settingsData);
      setAudit(auditData?.audit || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSave = async (key, value, reason) => {
    setSaving(true);
    try {
      await api.put(`/admin/system/settings/${key}`, { value: Number(value), reason });
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 p-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!data) return null;

  const u = data.usage || {};

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="admin-system-settings">
      <header className="flex items-start gap-3">
        <div className="rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/30 p-2.5">
          <Settings className="h-5 w-5 text-[#00E5FF]" />
        </div>
        <div>
          <h1 className="text-[22px] font-semibold text-white">System settings</h1>
          <p className="text-[13px] text-zinc-400 mt-1">
            Live spending controls. Changes take effect immediately on the next extraction call — no service restart needed.
            Every change is audit-logged. Super-admin only.
          </p>
        </div>
      </header>

      <section>
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Today&apos;s usage</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatTile label="Extractions today" value={u.extractions_today?.toLocaleString() || 0}
                    hint="Successful LLM doc extractions across all merchants" />
          <StatTile label="Merchants touched" value={u.merchants_touched_today?.toLocaleString() || 0}
                    hint="Distinct merchants with ≥1 extraction today" />
          <StatTile label="Cost today" value={`$${Number(u.org_cost_today_usd || 0).toFixed(2)}`}
                    hint="Sum of cost_usd across all extractions today"
                    accent={u.org_cost_today_usd >= (data.settings.find(s => s.key === "org_daily_budget_usd")?.current_value || 0) * 0.8 ? "text-amber-200" : null} />
        </div>
      </section>

      <section>
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Spending limits</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.settings.map((s) => (
            <SettingCard key={s.key} setting={s} onSave={onSave} saving={saving} />
          ))}
        </div>
      </section>

      <section>
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-2 flex items-center gap-2">
          <History className="h-3.5 w-3.5" /> Audit trail
          <span className="text-zinc-600">(last 50 changes)</span>
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
          {audit.length === 0 ? (
            <div className="p-6 text-center text-[12px] text-zinc-500">No changes yet.</div>
          ) : (
            <table className="w-full text-[12.5px]" data-testid="audit-table">
              <thead className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 bg-white/[0.02]">
                <tr>
                  <th className="text-left px-4 py-2.5">When</th>
                  <th className="text-left px-4 py-2.5">Setting</th>
                  <th className="text-left px-4 py-2.5">Change</th>
                  <th className="text-left px-4 py-2.5">By</th>
                  <th className="text-left px-4 py-2.5">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {audit.map((row) => (
                  <tr key={row.audit_id} data-testid={`audit-row-${row.audit_id}`}>
                    <td className="px-4 py-2.5 text-zinc-400 whitespace-nowrap">{new Date(row.changed_at).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-zinc-200">{row.key}</td>
                    <td className="px-4 py-2.5 text-zinc-300 tabular-nums">
                      <span className="text-zinc-500">{String(row.prior_value)}</span>
                      <span className="mx-1.5 text-zinc-600">→</span>
                      <span className="text-white">{String(row.new_value)}</span>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-300">{row.changed_by}</td>
                    <td className="px-4 py-2.5 text-zinc-400">{row.reason || <span className="text-zinc-600">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
